import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useImproveCycleStore } from '../../store/improveCycleStore'
import { CheckCircle, AlertCircle, FileText, RefreshCw } from 'lucide-react'

const ActuarForm = ({ ciclo }) => {
  const navigate = useNavigate()
  const { estandarizar, rePlanificar, loading } = useImproveCycleStore()
  const [showEstandarizarModal, setShowEstandarizarModal] = useState(false)
  const [showRePlanificarModal, setShowRePlanificarModal] = useState(false)
  const [nombrePOE, setNombrePOE] = useState('')
  const [motivoRelanzamiento, setMotivoRelanzamiento] = useState('')
  const [mensaje, setMensaje] = useState(null)

  const handleEstandarizar = async () => {
    if (!nombrePOE.trim()) {
      setMensaje({ type: 'error', text: 'Ingresa un nombre para el POE' })
      return
    }

    try {
      const result = await estandarizar(ciclo.id, nombrePOE, true)
      setMensaje({ type: 'success', text: '¡POE generado exitosamente! Ciclo completado.' })
      setShowEstandarizarModal(false)
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const handleRePlanificar = async () => {
    if (!motivoRelanzamiento.trim()) {
      setMensaje({ type: 'error', text: 'Ingresa el motivo del relanzamiento' })
      return
    }

    try {
      const result = await rePlanificar(ciclo.id, motivoRelanzamiento)
      setMensaje({ type: 'success', text: `¡CICLO-${String(result.nuevo_ciclo.numero_ciclo).padStart(2, '0')} creado exitosamente!` })
      setShowRePlanificarModal(false)
      
      // Navegar al nuevo ciclo después de 2 segundos
      setTimeout(() => {
        navigate(`/ciclos/${result.nuevo_ciclo.id}`)
      }, 2000)
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  if (ciclo?.fases?.actuar?.completado) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-800 mb-2">
            ✓ Fase Actuar completada
          </h3>
          <p className="text-green-700">
            Decisión: <span className="font-semibold capitalize">
              {ciclo.fases.actuar.decision === 'estandarizar' ? 'Estandarizar (POE Generado)' : 'Re-planificar (Nuevo Ciclo Creado)'}
            </span>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {mensaje && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${
          mensaje.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {mensaje.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          )}
          <p className={mensaje.type === 'success' ? 'text-green-700' : 'text-red-700'}>
            {mensaje.text}
          </p>
        </div>
      )}

      <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
        <h3 className="text-xl font-bold text-gray-800 mb-2">Decisión Final</h3>
        <p className="text-gray-600 mb-6">
          Basándote en los resultados de la verificación, decide el siguiente paso:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Estandarizar */}
          <button
            onClick={() => setShowEstandarizarModal(true)}
            className="group relative bg-white p-8 rounded-xl border-2 border-green-300 hover:border-green-500 hover:shadow-xl transition-all duration-200"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-2">✅ Estandarizar</h4>
              <p className="text-gray-600 text-sm">
                El objetivo fue alcanzado. Genera un POE para documentar el proceso mejorado.
              </p>
            </div>
          </button>

          {/* Re-planificar */}
          <button
            onClick={() => setShowRePlanificarModal(true)}
            className="group relative bg-white p-8 rounded-xl border-2 border-blue-300 hover:border-blue-500 hover:shadow-xl transition-all duration-200"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <RefreshCw className="w-10 h-10 text-blue-600" />
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-2">🔄 Re-planificar</h4>
              <p className="text-gray-600 text-sm">
                El objetivo no fue alcanzado. Crea un nuevo ciclo con las lecciones aprendidas.
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Modal Estandarizar */}
      {showEstandarizarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Generar POE</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del POE *
              </label>
              <input
                type="text"
                value={nombrePOE}
                onChange={(e) => setNombrePOE(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Ej: POE Reducción de Tiempos de Espera"
                autoFocus
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                Se generará un Procedimiento Operativo Estándar con toda la información del ciclo PDCA.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleEstandarizar}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300"
              >
                Generar POE
              </button>
              <button
                onClick={() => setShowEstandarizarModal(false)}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Re-planificar */}
      {showRePlanificarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Crear Nuevo Ciclo</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo del Relanzamiento *
              </label>
              <textarea
                value={motivoRelanzamiento}
                onChange={(e) => setMotivoRelanzamiento(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Explica por qué se necesita un nuevo ciclo y qué se hará diferente..."
                autoFocus
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                Se creará un nuevo ciclo (CICLO-{String(ciclo.numero_ciclo + 1).padStart(2, '0')}) con las lecciones aprendidas precargadas.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleRePlanificar}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
              >
                Crear Nuevo Ciclo
              </button>
              <button
                onClick={() => setShowRePlanificarModal(false)}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ActuarForm
